import React from "react";
import styled, { css, keyframes } from "styled-components";

import {
  CELEBRATION_MS,
  COUNT_IN_EXIT_MS,
  MOBILE_BREAKPOINT,
} from "../../constants";
import Panel from "../Panel";

// Presentational. All state + state-machine logic lives in
// MarcEngine (per-marc) and SpeedrunEngine (round); this component
// just renders snapshots.
function ScorePanel({ snapshot, bindBar, roundSnapshot, countIn }) {
  const { celebrating, hasLabeled, regions } = snapshot;

  // Pre-round: timer hasn't started yet. Egg is flying in, reference
  // might still be loading, or the 3-2-1-GO sequence is rolling. We
  // dim the panel content and show "…" in the count-in slot until
  // the active numeral takes over. Gate on startedAt alone (not
  // total > 0) so the overlay covers the first render after mode
  // flips to SPEEDRUN — before the engine's start() effect runs.
  const beforeRoundStart = roundSnapshot?.startedAt == null;
  const numeral = countIn?.numeral ?? (beforeRoundStart ? "…" : null);
  const showOverlay = numeral != null;
  // Dim while the overlay is fully on — exit fade lifts the dim so
  // the panel content swells back up as the numeral fades out.
  const dim = showOverlay && !countIn?.exiting;

  const barRef = React.useRef(null);
  React.useEffect(() => {
    bindBar(barRef.current);
  });

  return (
    <StyledPanel>
      <FlashOverlay $on={celebrating} />
      <Stack $dim={dim}>
        <TopLine>
          <Title>{roundLabel(roundSnapshot)}</Title>
          <RoundTimer
            startedAt={roundSnapshot?.startedAt ?? null}
            endedAt={roundSnapshot?.endedAt ?? null}
          />
        </TopLine>
        <Body>
          <BodyContent snapshot={snapshot} barRef={barRef} />
        </Body>
        {hasLabeled && (
          <Pips>
            {regions.map((r) => (
              <Pip key={r.id} $on={r.detected} title={r.label} />
            ))}
          </Pips>
        )}
      </Stack>
      {showOverlay && (
        <CountInOverlay $exiting={countIn?.exiting ?? false}>
          {/* keyed remount per beat so the scale-in animation re-plays */}
          <CountInNumeral key={numeral} $waiting={countIn?.numeral == null}>
            {numeral}
          </CountInNumeral>
        </CountInOverlay>
      )}
    </StyledPanel>
  );
}

const StyledPanel = styled(Panel)`
  padding: 10px 16px 12px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 6px 12px 8px;
  }
`;

// Count-up timer. Drives its own rAF loop and writes textContent
// directly to the DOM, so the panel doesn't re-render at 30fps. The
// round freezes the displayed time at endedAt - startedAt the moment
// SpeedrunEngine flips complete.
function RoundTimer({ startedAt, endedAt }) {
  const elRef = React.useRef(null);

  React.useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    if (startedAt == null) {
      el.textContent = formatElapsed(0);
      return;
    }
    if (endedAt != null) {
      el.textContent = formatElapsed(endedAt - startedAt);
      return;
    }
    let raf = 0;
    const tick = () => {
      el.textContent = formatElapsed(Date.now() - startedAt);
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [startedAt, endedAt]);

  return <Time ref={elRef}>{formatElapsed(0)}</Time>;
}

// MM:SS.t — minutes : seconds . tenths. Tenths precision is enough at
// the speedrun pace (rounds run ~30-90s); milliseconds would jitter
// without adding signal.
function formatElapsed(ms) {
  const total = Math.max(0, ms);
  const totalTenths = Math.floor(total / 100);
  const tenths = totalTenths % 10;
  const totalSeconds = Math.floor(totalTenths / 10);
  const seconds = totalSeconds % 60;
  const minutes = Math.floor(totalSeconds / 60);
  const mm = String(minutes).padStart(2, "0");
  const ss = String(seconds).padStart(2, "0");
  return `${mm}:${ss}.${tenths}`;
}

// Top-line round counter. Pre-round (no lineup) reads as just "Marc".
function roundLabel(roundSnapshot) {
  const total = roundSnapshot?.total ?? 0;
  if (total === 0) return "Marc";
  // currentIndex is 0-based mid-round; parks on the last index when the
  // round completes, so cap displayed N at total.
  const current = Math.min(roundSnapshot.currentIndex + 1, total);
  return `Marc ${current} of ${total}`;
}

// Three body states, dispatched in order:
//   1. empty   — reference not yet loaded (cache miss). Render nothing;
//                the count-in overlay covers the panel during this
//                window so an empty body never shows through.
//   2. done    — every region detected, marc complete
//   3. active  — drawing in progress (further branches inside)
function BodyContent({ snapshot, barRef }) {
  const { hasLabeled, allDone, shownLabel } = snapshot;

  if (!hasLabeled) return null;
  // Mirror ActiveInstruction's outer shape (caption + TargetSlot + a
  // bar-shaped slot) so the panel doesn't shrink between the last
  // celebration and "marc complete." The bar slot is solid-filled
  // instead of striped — signal "done" without restarting the diagonal
  // scroll.
  if (allDone) {
    return (
      <Instruction>
        <DrawThe>You drew</DrawThe>
        <TargetSlot>
          <Target>Marc!</Target>
        </TargetSlot>
        <ProgressTrack>
          <DoneFill />
        </ProgressTrack>
      </Instruction>
    );
  }
  if (!shownLabel) return null;

  return <ActiveInstruction snapshot={snapshot} barRef={barRef} />;
}

// Active drawing UI: small caption + the rotating big label + progress
// bar. The big-label slot has its own three-way render — overdraw
// override, mid-celebration crossfade, settled — pulled out into
// TargetContent so each branch reads one-line.
function ActiveInstruction({ snapshot, barRef }) {
  const { shownOverdrawn } = snapshot;
  const caption = shownOverdrawn ? "that's too much" : "Draw His";

  return (
    <Instruction>
      <DrawThe $alert={shownOverdrawn}>{caption}</DrawThe>
      <TargetSlot>
        <TargetContent snapshot={snapshot} />
      </TargetSlot>
      <ProgressTrack>
        <ProgressFill ref={barRef} />
      </ProgressTrack>
    </Instruction>
  );
}

function TargetContent({ snapshot }) {
  const { shownOverdrawn, celebrating, shownLabel, incomingLabel } = snapshot;

  if (shownOverdrawn)
    return (
      <Target key="overdrawn" $overdrawn>
        {shownLabel}
      </Target>
    );

  if (celebrating) {
    return (
      <>
        <Target key="exiting" $exiting>
          {shownLabel}
        </Target>
        {incomingLabel && (
          <Target key="entering" $entering>
            {incomingLabel}
          </Target>
        )}
      </>
    );
  }

  return <Target key="settled">{shownLabel}</Target>;
}

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  /* justify-content: space-between; */
  height: 100%;
  /* gap: 0.5rem; */
  transition: opacity ${COUNT_IN_EXIT_MS}ms ease-out;
  opacity: ${(p) => (p.$dim ? 0.15 : 1)};
`;

const TopLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-direction: row;
`;

const Time = styled.div`
  /* font-size: 11px; */
  font-size: 1.125rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  /* color: var(--color-text-muted); */
`;

const Title = styled.div`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-text-muted);
`;

const Body = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  min-height: 0;
  padding: 1.25rem 0;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 0.5rem 0;
  }
`;

const DrawThe = styled.span`
  font-weight: 600;
  font-size: 12px;
  text-align: center;
  text-transform: uppercase;
  color: var(--color-text-muted);
  line-height: 1;

  ${(p) =>
    p.$alert &&
    css`
      color: var(--color-alert);
      /* font-size: 14px; */
      letter-spacing: 0.04em;
    `}
`;

const Instruction = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  /* gap: 0.25rem; */
`;

// Stack two Targets in the same grid cell so the outgoing label can slide
// up and out while the incoming label slides up from below — no manual
// absolute positioning per label needed.
const TargetSlot = styled.div`
  display: grid;
  grid-template-areas: "stack";
  align-items: center;
  justify-items: center;
  overflow: hidden;
`;

const slideUpOut = keyframes`
  0% { transform: translateY(0); opacity: 1; }
  100% { transform: translateY(-110%); opacity: 0; }
`;

const slideUpIn = keyframes`
  0% { transform: translateY(110%); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Target = styled.span`
  grid-area: stack;
  font-family: "Fraunces", serif;
  font-size: 3.25rem;
  font-weight: 600;
  text-align: center;
  line-height: 1.1;
  text-transform: uppercase;
  color: var(--color-orange-500);
  text-shadow:
    0 1px 0 rgba(255, 255, 255, 0.4),
    0 2px 0 var(--color-text);

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 2.4rem;
  }

  ${(p) =>
    p.$exiting &&
    css`
      animation: ${slideUpOut} ${CELEBRATION_MS}ms ease-out forwards;
    `}
  ${(p) =>
    p.$entering &&
    css`
      animation: ${slideUpIn} ${CELEBRATION_MS}ms ease-out forwards;
    `}

  ${(p) =>
    p.$overdrawn &&
    css`
      color: #ff6467;
    `}

  @media (prefers-reduced-motion: reduce) {
    ${(p) =>
      p.$exiting &&
      css`
        animation: ${fadeOut} ${CELEBRATION_MS}ms ease-out forwards !important;
      `}
    ${(p) =>
      p.$entering &&
      css`
        animation: ${fadeIn} ${CELEBRATION_MS}ms ease-out forwards !important;
      `}
  }
`;

const ProgressTrack = styled.div`
  width: 280px;
  height: 12px;
  border-radius: 2px;
  margin-top: 0.5rem;
  background: var(--color-orange-200);
  /* overflow: hidden; */
  position: relative;
  outline: 1px solid var(--color-text);

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    margin-top: 0.25rem;
    height: 10px;
  }
`;

const progressFillAnimation = keyframes`
  to {
    background-position: 24px 0;
  }
`;

const ProgressFill = styled.div`
  /* The fill spans the full track; the DetectionGate writes
     clip-path: inset(0 N% 0 0) directly to this element to reveal more
     of the bar as the displayed score rises. clip-path leaves the
     rotated stripe pseudo unscaled (a width / scaleX animation would
     shear the diagonal). */
  position: absolute;
  inset: 0;
  clip-path: inset(0 100% 0 0);
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 200%;
    height: 500px;
    background:
      repeating-linear-gradient(
        90deg,
        var(--color-orange-400) 0 12px,
        rgba(255, 255, 255, 0.4) 12px 24px
      ),
      var(--color-orange-500);
    background-size: 24px 24px;
    transform: translate(-50%, -50%) rotate(45deg);
    animation: ${progressFillAnimation} 2000ms linear infinite;
    will-change: background-position;
  }

  /* Overdraw: same striped fill but flipped to alert red. The gate
     forces the bar to full when overdrawn, so the player sees a
     conspicuous solid-red bar instead of orange — paired with the
     "you drew too much" title swap above. */
  &[data-overdrawn="1"]::before {
    background:
      repeating-linear-gradient(
        90deg,
        var(--color-alert-stripe) 0 12px,
        rgba(255, 255, 255, 0.4) 12px 24px
      ),
      var(--color-alert);
    /* background: var(--color-alert); */
    background-size: 24px 24px;
    animation-play-state: paused;
  }

  @media (prefers-reduced-motion: reduce) {
    &::before {
      animation: none;
    }
  }

  /* Pause the diagonal scroll while the bar is climbing toward raw
     target — two simultaneous animations (clip-path reveal + stripe
     scroll) read as visual noise. Resume on stable / satisfied,
     signaling "you're holding at this level, keep going." Driven by a
     data attribute the gate writes alongside clip-path. */
  &[data-growing="1"]::before {
    animation-play-state: paused;
  }
`;

const DoneFill = styled.div`
  position: absolute;
  inset: 0;
  background: var(--color-orange-500);
`;

const Pips = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
`;

const Pip = styled.span`
  opacity: 0.8;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${(p) =>
    p.$on ? "var(--color-orange-500)" : "var(--color-stone-300)"};
  border: 1px solid
    ${(p) => (p.$on ? "var(--color-orange-500)" : "var(--color-border)")};
`;

// Diagonal light sweep across the panel on commit. Sits above the
// content so the gradient washes over labels + bar; pointer-events:
// none so it can't block input. Self-clips with overflow: hidden +
// matching border-radius (the parent Panel's Interior is
// border-radius: 6px and doesn't clip its children).
const flashSweep = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

// 3-2-1-GO takeover. Sits over the dimmed Stack. The numeral itself
// scale/fades in per beat (key change → remount); the overlay container
// fades the whole thing out during the exit window when markStarted has
// already fired and the panel content is fading back to full opacity.
const countInPop = keyframes`
  0%   { transform: scale(0.55); opacity: 0; }
  60%  { transform: scale(1.08); opacity: 1; }
  100% { transform: scale(1);    opacity: 1; }
`;

const CountInOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 2;
  transition: opacity ${COUNT_IN_EXIT_MS}ms ease-out;
  opacity: ${(p) => (p.$exiting ? 0 : 1)};
`;

const CountInNumeral = styled.span`
  font-family: "Fraunces", serif;
  font-size: 5rem;
  font-weight: 600;
  line-height: 1;
  letter-spacing: -0.02em;
  color: var(--color-orange-500);
  text-shadow:
    0 1px 0 rgba(255, 255, 255, 0.4),
    0 2px 0 var(--color-text);
  animation: ${countInPop} 280ms cubic-bezier(0.34, 1.56, 0.64, 1) both;

  ${(p) =>
    p.$waiting &&
    css`
      /* Waiting placeholder ("…") reads as secondary chrome — same
         slot as the numerals, but lighter and smaller so it doesn't
         compete with the 3-2-1-GO that's about to land. */
      font-size: 3rem;
      font-weight: 500;
      color: var(--color-text-muted);
      text-shadow: none;
      letter-spacing: 0.1em;
    `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const FlashOverlay = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 6px;
  overflow: hidden;
  pointer-events: none;
  z-index: 1;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      115deg,
      transparent 35%,
      rgba(255, 255, 255, 0.55) 50%,
      transparent 65%
    );
    transform: translateX(-100%);
    ${(p) =>
      p.$on &&
      css`
        animation: ${flashSweep} ${CELEBRATION_MS}ms ease-out forwards;
      `}
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation: none;
    }
  }
`;

export default ScorePanel;
